/**
 * Clock (Horario) design-store catalog. Mirrors the other widget catalogs: a
 * static gallery of self-contained card SVGs the user can apply to the Clock
 * (DATE_TIME) widget. Recreated 1:1 from the Claude Design `Clock Widgets.html`
 * handoff (10 variants), cropped from the 1920×1080 scene to the clock card.
 *
 * PURE-DATA FORMAT: every design is now a {@link SerializableDesign} (plain
 * data — no JS `build()`), so designs can be stored / transmitted / PUBLISHED
 * safely. The generic {@link renderDesign} turns the data into the final SVG.
 * The card SVGs keep the original ABSOLUTE scene coordinates and just set
 * `viewBox` to the card crop (`x y w h`) — no re-basing. Each defs / gradient /
 * filter / pattern id is prefixed with the design id so multiple cards inline
 * without collisions. `renderClockDesignSvg(design, parts)` is the single entry
 * point the Store thumbnail, the config preview and the live Pixi renderer all
 * call; `computeClockParts(now, opts)` derives every piece from a Date in the
 * widget's timezone (respecting the 12/24h format).
 *
 * The values come from `Intl.DateTimeFormat` over a fixed timezone id (not free
 * user text), so no XSS escaping is needed (text slots are declared
 * `escape:false`). Where the seconds drive geometry (the cyber tick bar) the
 * value is also bound numerically so the `computed` formula can read it.
 *
 * Known caveats: the Editorial + Cottagecore analog dials keep the mock's fixed
 * hand angles (decorative; the digital time is the live part). CJK accents
 * (時刻 / 土曜日) fall back to a system face for fonts (VT323/Orbitron) lacking
 * those glyphs — matches the mock.
 */

import { renderDesign } from "../entities/designs/renderDesign";
import { getInstalledDesign } from "../entities/designs/installedRegistry";
import type {
  DesignSlot,
  SerializableDesign,
} from "../entities/designs/serializableDesign";

export type ClockDesignCategory = "clock";

/** Everything a clock card might display, derived from the current instant. */
export interface ClockParts {
  /** Zero-padded hour/minute (hour respects the 12/24h format). */
  hh: string;
  mm: string;
  /** "HH:MM". */
  time: string;
  /** Individual digits of HH:MM (for the flip design). */
  h1: string;
  h2: string;
  m1: string;
  m2: string;
  /** Zero-padded seconds. */
  seconds: string;
  /** "AM" / "PM" (always available, even in 24h mode). */
  ampm: string;
  weekdayLong: string;
  weekdayShort: string;
  weekdayJa: string;
  weekdayLongLower: string;
  day: string;
  day2: string;
  monthShort: string;
  monthLong: string;
  monthLongLower: string;
  year: string;
  /** Short offset, e.g. "GMT-3". */
  gmt: string;
  /** Roman-numeral year, e.g. "MMXXVI". */
  roman: string;
}

/**
 * A clock design is a {@link SerializableDesign} pinned to `widgetType:"clock"`,
 * plus the clock-store metadata the gallery/renderer need (`usesSeconds` drives
 * the re-raster cadence; the SerializableDesign always carries `viewBox`).
 */
export interface ClockDesign extends SerializableDesign {
  widgetType: "clock";
  category: ClockDesignCategory;
  viewBox: string;
  /** Whether the design renders seconds (drives the re-raster cadence). */
  usesSeconds: boolean;
}

const pad2 = (n: number): string => String(n).padStart(2, "0");

const romanize = (num: number): string => {
  if (!Number.isFinite(num) || num <= 0) return "";
  const map: [number, string][] = [
    [1000, "M"],
    [900, "CM"],
    [500, "D"],
    [400, "CD"],
    [100, "C"],
    [90, "XC"],
    [50, "L"],
    [40, "XL"],
    [10, "X"],
    [9, "IX"],
    [5, "V"],
    [4, "IV"],
    [1, "I"],
  ];
  let n = num;
  let out = "";
  for (const [v, s] of map) {
    while (n >= v) {
      out += s;
      n -= v;
    }
  }
  return out;
};

/** Derive all clock parts for `now` in the given timezone + 12/24h format. */
export function computeClockParts(
  now: Date,
  opts: { timeZone?: string; hour12?: boolean },
): ClockParts {
  const tz = opts.timeZone || undefined;
  const hour12 = !!opts.hour12;

  const timeParts = new Intl.DateTimeFormat("en-GB", {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: hour12 ? "h12" : "h23",
  }).formatToParts(now);
  const get = (t: string) => timeParts.find((x) => x.type === t)?.value ?? "00";
  const hh = get("hour");
  const mm = get("minute");
  const seconds = get("second");

  const ampm = (
    new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hour: "numeric",
      hour12: true,
    })
      .formatToParts(now)
      .find((x) => x.type === "dayPeriod")?.value ?? ""
  ).toUpperCase();

  const es = (o: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat("es-AR", { timeZone: tz, ...o }).format(now);
  const weekdayLong = es({ weekday: "long" });
  const weekdayShort = es({ weekday: "short" }).replace(/\./g, "");
  const monthShort = es({ month: "short" }).replace(/\./g, "");
  const monthLong = es({ month: "long" });
  const day = es({ day: "numeric" });
  const year = es({ year: "numeric" });
  const weekdayJa = new Intl.DateTimeFormat("ja-JP", {
    timeZone: tz,
    weekday: "long",
  }).format(now);
  const gmt =
    new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "shortOffset",
    })
      .formatToParts(now)
      .find((x) => x.type === "timeZoneName")?.value ?? "";

  return {
    hh,
    mm,
    time: `${hh}:${mm}`,
    h1: hh[0] ?? "0",
    h2: hh[1] ?? "0",
    m1: mm[0] ?? "0",
    m2: mm[1] ?? "0",
    seconds,
    ampm,
    weekdayLong: weekdayLong.toUpperCase(),
    weekdayShort: weekdayShort.toUpperCase(),
    weekdayJa,
    weekdayLongLower: weekdayLong.toLowerCase(),
    day,
    day2: pad2(Number(day) || 0),
    monthShort: monthShort.toUpperCase(),
    monthLong: monthLong.toUpperCase(),
    monthLongLower: monthLong.toLowerCase(),
    year,
    gmt,
    roman: romanize(Number(year) || 0),
  };
}

// All clock parts are Intl-derived (a fixed timezone id, not free user text),
// so text slots are NOT escaped — they substitute verbatim, byte-for-byte with
// the legacy `${p.x}` interpolation (the renderer's escaping choke-point is a
// no-op for these system strings, but we declare it explicitly).
const T: DesignSlot = { kind: "text", escape: false };

// ── 01 · Dark Minimal ──────────────────────────────────────────────────────
const darkDesign: ClockDesign = {
  id: "dark",
  name: "Dark Minimal",
  subtitle: "Pill clock",
  widgetType: "clock",
  category: "clock",
  description:
    "Charcoal rounded chip with a live dot, big mono HH:MM, coloured colon, small seconds and the weekday.",
  swatches: ["#0d0d12", "#15151d", "#a07dff", "#3ad96b"],
  viewBox: "610 410 700 260",
  cardWidth: 700,
  cardHeight: 260,
  fonts: ["DM Mono", "Inter"],
  usesSeconds: true,
  slots: {
    gmt: T,
    hh: T,
    mm: T,
    seconds: T,
    weekdayLong: T,
    day: T,
    monthShort: T,
  },
  frame: `<svg viewBox="610 410 700 260" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="dark-ckd-panel" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#16161e"/><stop offset="100%" stop-color="#0b0b10"/></linearGradient>
    <filter id="dark-ckd-shadow" x="-20%" y="-20%" width="140%" height="160%"><feDropShadow dx="0" dy="16" stdDeviation="24" flood-color="#000" flood-opacity=".55"/></filter>
  </defs>
  <g transform="translate(610 410)">
    <rect width="700" height="260" rx="34" fill="url(#dark-ckd-panel)" stroke="#26262f" stroke-width="1.5" filter="url(#dark-ckd-shadow)"/>
    <rect x="1.5" y="1.5" width="697" height="80" rx="32" fill="#fff" opacity=".03"/>
    <g transform="translate(48 54)"><circle r="8" fill="#3ad96b"/><circle r="13" fill="none" stroke="#3ad96b" stroke-width="1.5" opacity=".5"/></g>
    <text x="76" y="62" font-family="DM Mono, monospace" font-weight="500" font-size="22" fill="#8a8f9c" letter-spacing="6">HORA · EN VIVO</text>
    <text x="652" y="62" text-anchor="end" font-family="DM Mono, monospace" font-size="20" fill="#a07dff">{{gmt}}</text>
    <text x="48" y="196" font-family="DM Mono, monospace" font-weight="500" font-size="150" fill="#f5f5f5" letter-spacing="2">{{hh}}<tspan fill="#a07dff">:</tspan>{{mm}}</text>
    <text x="560" y="196" font-family="DM Mono, monospace" font-weight="500" font-size="60" fill="#5a5e6b">{{seconds}}</text>
    <text x="652" y="232" text-anchor="end" font-family="Inter, sans-serif" font-weight="600" font-size="22" fill="#8a8f9c">{{weekdayLong}} · {{day}} {{monthShort}}</text>
  </g>
</svg>`,
};

// ── 02 · Flip Clock ────────────────────────────────────────────────────────
const flipDesign: ClockDesign = {
  id: "flip",
  name: "Flip Clock",
  subtitle: "Split-flap",
  widgetType: "clock",
  category: "clock",
  description:
    "Split-flap tiles with center hinge lines and rounded caps, amber label and a date strip beneath.",
  swatches: ["#15151a", "#26262e", "#f5f5f5", "#ffb703"],
  viewBox: "560 400 800 280",
  cardWidth: 800,
  cardHeight: 280,
  fonts: ["DM Mono"],
  usesSeconds: false,
  slots: {
    h1: T,
    h2: T,
    m1: T,
    m2: T,
    weekdayShort: T,
    day2: T,
    monthShort: T,
    year: T,
  },
  frame: `<svg viewBox="560 400 800 280" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="flip-ckd-tile" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#33333d"/><stop offset="49%" stop-color="#26262e"/><stop offset="51%" stop-color="#1a1a20"/><stop offset="100%" stop-color="#202028"/></linearGradient>
    <filter id="flip-ckd-shadow" x="-20%" y="-20%" width="140%" height="160%"><feDropShadow dx="0" dy="16" stdDeviation="22" flood-color="#000" flood-opacity=".55"/></filter>
  </defs>
  <g transform="translate(560 400)">
    <rect width="800" height="280" rx="24" fill="#15151a" filter="url(#flip-ckd-shadow)"/>
    <text x="400" y="56" text-anchor="middle" font-family="DM Mono, monospace" font-size="18" fill="#ffb703" letter-spacing="14">HORA LOCAL</text>
    <g transform="translate(60 84)">
      <g id="flip-ckd-t"><rect width="150" height="150" rx="14" fill="url(#flip-ckd-tile)" stroke="#0a0a0d" stroke-width="2"/><line x1="0" y1="75" x2="150" y2="75" stroke="#0a0a0d" stroke-width="3"/><rect x="8" y="8" width="134" height="64" rx="8" fill="#fff" opacity=".05"/><circle cx="-2" cy="75" r="5" fill="#0a0a0d"/><circle cx="152" cy="75" r="5" fill="#0a0a0d"/></g>
      <use href="#flip-ckd-t" transform="translate(170 0)"/>
      <use href="#flip-ckd-t" transform="translate(420 0)"/>
      <use href="#flip-ckd-t" transform="translate(590 0)"/>
      <text x="385" y="100" text-anchor="middle" font-family="DM Mono, monospace" font-weight="500" font-size="80" fill="#ffb703">:</text>
      <g font-family="DM Mono, monospace" font-weight="500" font-size="96" fill="#f5f5f5" text-anchor="middle"><text x="75" y="116">{{h1}}</text><text x="245" y="116">{{h2}}</text><text x="495" y="116">{{m1}}</text><text x="665" y="116">{{m2}}</text></g>
    </g>
    <text x="400" y="262" text-anchor="middle" font-family="DM Mono, monospace" font-size="18" fill="#8a8a92" letter-spacing="6">{{weekdayShort}} · {{day2}} · {{monthShort}} · {{year}}</text>
  </g>
</svg>`,
};

// ── 03 · Y2K Aqua Gel ──────────────────────────────────────────────────────
const y2kDesign: ClockDesign = {
  id: "y2k",
  name: "Y2K Aqua Gel",
  subtitle: "Gel readout",
  widgetType: "clock",
  category: "clock",
  description:
    "Glossy aqua capsule with chrome highlight, gel digits and a green LED indicator. Bubbly Audiowide.",
  swatches: ["#003a7a", "#1c8fff", "#8acfff", "#5aff8a"],
  viewBox: "610 410 700 260",
  cardWidth: 700,
  cardHeight: 260,
  fonts: ["Audiowide", "DM Mono"],
  usesSeconds: false,
  slots: {
    gmt: T,
    time: T,
    weekdayLong: T,
    day2: T,
    monthShort: T,
    year: T,
  },
  frame: `<svg viewBox="610 410 700 260" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="y2k-ckd-bg" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#cfeaff"/><stop offset="55%" stop-color="#1c8fff"/><stop offset="100%" stop-color="#003a7a"/></linearGradient>
    <filter id="y2k-ckd-shadow" x="-20%" y="-20%" width="140%" height="160%"><feDropShadow dx="0" dy="16" stdDeviation="22" flood-color="#001026" flood-opacity=".5"/></filter>
  </defs>
  <g transform="translate(610 410)">
    <rect width="700" height="260" rx="48" fill="url(#y2k-ckd-bg)" stroke="#fff" stroke-width="3" filter="url(#y2k-ckd-shadow)"/>
    <ellipse cx="350" cy="34" rx="310" ry="14" fill="#fff" opacity=".7"/>
    <g transform="translate(48 56)"><circle r="11" fill="#003a7a"/><circle r="6" fill="#5aff8a"/></g>
    <text x="76" y="64" font-family="Audiowide, sans-serif" font-size="24" fill="#fff" letter-spacing="2">TIME.NOW</text>
    <text x="652" y="64" text-anchor="end" font-family="Audiowide, sans-serif" font-size="18" fill="#cffff4">{{gmt}}</text>
    <text x="350" y="190" text-anchor="middle" font-family="Audiowide, sans-serif" font-size="128" fill="#fff" letter-spacing="2">{{time}}</text>
    <text x="350" y="232" text-anchor="middle" font-family="DM Mono, monospace" font-size="18" fill="#cffff4" letter-spacing="8">{{weekdayLong}} · {{day2}} {{monthShort}} {{year}}</text>
  </g>
</svg>`,
};

// ── 04 · Cyberpunk Neon ────────────────────────────────────────────────────
const cyberDesign: ClockDesign = {
  id: "cyber",
  name: "Cyberpunk Neon",
  subtitle: "HUD time",
  widgetType: "clock",
  category: "clock",
  description:
    "Chamfered HUD with neon Orbitron time, katakana tag, a seconds tick bar and scanlines.",
  swatches: ["#080010", "#00f0ff", "#ff2e88", "#f5d019"],
  viewBox: "610 410 700 260",
  cardWidth: 700,
  cardHeight: 260,
  fonts: ["Orbitron", "DM Mono"],
  usesSeconds: true,
  slots: {
    time: T,
    seconds: T,
    gmt: T,
  },
  // tickW = round(620 * seconds / 60). The `seconds` value is bound as a text
  // slot for display AND auto-promoted to a numeric var by the renderer
  // (buildVars: Number("42") is finite), so this formula reads it directly.
  // A non-numeric seconds value would make evalFormula return NaN → the
  // renderer falls back tickW to 0, matching the legacy `Number(...) || 0`.
  computed: {
    tickW: "round(620 * seconds / 60)",
  },
  frame: `<svg viewBox="610 410 700 260" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <filter id="cyber-ckd-g"><feGaussianBlur stdDeviation="2.5"/><feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge></filter>
    <pattern id="cyber-ckd-scan" width="100%" height="3" patternUnits="userSpaceOnUse"><rect width="100%" height="1" fill="#00f0ff" opacity=".07"/></pattern>
  </defs>
  <g transform="translate(610 410)" filter="url(#cyber-ckd-g)">
    <polygon points="0,40 40,0 660,0 700,40 700,220 660,260 40,260 0,220" fill="#080010" stroke="#ff2e88" stroke-width="2.5"/>
    <polygon points="14,46 50,14 650,14 686,46 686,214 650,246 50,246 14,214" fill="none" stroke="#00f0ff" stroke-width="1.5" opacity=".7"/>
    <rect x="14" y="14" width="672" height="232" fill="url(#cyber-ckd-scan)"/>
    <text x="34" y="58" font-family="DM Mono, monospace" font-size="18" fill="#f5d019" letter-spacing="6">▮ SYS.CLOCK · 時刻</text>
    <text x="666" y="58" text-anchor="end" font-family="DM Mono, monospace" font-size="16" fill="#ff2e88">● LIVE</text>
    <text x="350" y="180" text-anchor="middle" font-family="Orbitron, sans-serif" font-weight="900" font-size="110" fill="#00f0ff" letter-spacing="2">{{time}}</text>
    <g transform="translate(40 210)"><rect width="620" height="6" fill="#0a2a30"/><rect width="{{tickW}}" height="6" fill="#ff2e88"/></g>
    <text x="40" y="240" font-family="DM Mono, monospace" font-size="14" fill="#88e8d8" letter-spacing="4">SEC {{seconds}} · {{gmt}} · @eriol</text>
  </g>
</svg>`,
};

// ── 05 · Editorial ─────────────────────────────────────────────────────────
const editorialDesign: ClockDesign = {
  id: "editorial",
  name: "Editorial",
  subtitle: "Analog + serif",
  widgetType: "clock",
  category: "clock",
  description:
    "Cream card with a fine analog dial, serif digital time and a dateline rule. Newspaper feel.",
  swatches: ["#f5efe2", "#1a1815", "#c8a050", "#8a3a2a"],
  viewBox: "610 410 700 260",
  cardWidth: 700,
  cardHeight: 260,
  fonts: ["Cormorant Garamond", "DM Mono"],
  usesSeconds: false,
  slots: {
    time: T,
    weekdayLong: T,
    day: T,
    monthLong: T,
    roman: T,
  },
  frame: `<svg viewBox="610 410 700 260" xmlns="http://www.w3.org/2000/svg">
  <defs><filter id="editorial-ckd-shadow" x="-20%" y="-20%" width="140%" height="160%"><feDropShadow dx="0" dy="14" stdDeviation="20" flood-color="#000" flood-opacity=".4"/></filter></defs>
  <g transform="translate(610 410)">
    <rect width="700" height="260" fill="#f5efe2" filter="url(#editorial-ckd-shadow)"/>
    <line x1="24" y1="24" x2="676" y2="24" stroke="#1a1815" stroke-width="3"/>
    <line x1="24" y1="32" x2="676" y2="32" stroke="#1a1815" stroke-width="1"/>
    <g transform="translate(130 150)">
      <circle r="86" fill="#fff" stroke="#1a1815" stroke-width="2"/>
      <circle r="86" fill="none" stroke="#c8a050" stroke-width="1" stroke-dasharray="2 6"/>
      <g stroke="#1a1815" stroke-width="2"><line x1="0" y1="-78" x2="0" y2="-68"/><line x1="0" y1="78" x2="0" y2="68"/><line x1="-78" y1="0" x2="-68" y2="0"/><line x1="78" y1="0" x2="68" y2="0"/></g>
      <text x="0" y="-56" text-anchor="middle" font-family="Cormorant Garamond, serif" font-size="16" fill="#1a1815">XII</text>
      <line x1="0" y1="0" x2="22" y2="-40" stroke="#1a1815" stroke-width="4" stroke-linecap="round"/>
      <line x1="0" y1="0" x2="-44" y2="20" stroke="#8a3a2a" stroke-width="3" stroke-linecap="round"/>
      <circle r="5" fill="#8a3a2a"/>
    </g>
    <text x="260" y="150" font-family="Cormorant Garamond, serif" font-style="italic" font-weight="600" font-size="96" fill="#1a1815">{{time}}</text>
    <line x1="260" y1="178" x2="652" y2="178" stroke="#1a1815" stroke-width="1"/>
    <text x="260" y="214" font-family="DM Mono, monospace" font-size="15" fill="#8a3a2a" letter-spacing="6">{{weekdayLong}} {{day}} DE {{monthLong}} · {{roman}}</text>
  </g>
</svg>`,
};

// ── 06 · Pixel Arcade ──────────────────────────────────────────────────────
const pixelDesign: ClockDesign = {
  id: "pixel",
  name: "Pixel Arcade",
  subtitle: "HUD clock",
  widgetType: "clock",
  category: "clock",
  description:
    "8-bit dialog box with Press Start 2P time, a clock sprite and a chunky AM/PM tab.",
  swatches: ["#1d2a3a", "#ffb703", "#48cae4", "#ff3d4d"],
  viewBox: "610 410 700 260",
  cardWidth: 700,
  cardHeight: 260,
  fonts: ["Press Start 2P"],
  usesSeconds: false,
  slots: {
    time: T,
    ampm: T,
    weekdayShort: T,
    day2: T,
    monthShort: T,
  },
  frame: `<svg viewBox="610 410 700 260" xmlns="http://www.w3.org/2000/svg" shape-rendering="crispEdges">
  <g transform="translate(610 410)">
    <rect width="700" height="260" fill="#48cae4"/>
    <rect x="8" y="8" width="684" height="244" fill="#1d2a3a"/>
    <rect x="16" y="16" width="668" height="228" fill="#0a121c"/>
    <g fill="#ffb703"><rect width="24" height="24"/><rect x="676" width="24" height="24"/><rect y="236" width="24" height="24"/><rect x="676" y="236" width="24" height="24"/></g>
    <g transform="translate(40 -16)"><rect width="200" height="32" fill="#ff3d4d"/><rect x="6" y="6" width="188" height="20" fill="#1d2a3a"/><text x="100" y="22" text-anchor="middle" font-family="Press Start 2P, monospace" font-size="12" fill="#ffb703">CLOCK</text></g>
    <g transform="translate(96 138)" fill="none" stroke="#ffb703" stroke-width="6"><rect x="-40" y="-40" width="80" height="80"/><line x1="0" y1="0" x2="0" y2="-26"/><line x1="0" y1="0" x2="20" y2="10"/></g>
    <text x="200" y="160" font-family="Press Start 2P, monospace" font-size="56" fill="#fff">{{time}}</text>
    <g transform="translate(560 90)"><rect width="100" height="40" fill="#ffb703"/><rect x="4" y="4" width="92" height="32" fill="#1d2a3a"/><text x="50" y="28" text-anchor="middle" font-family="Press Start 2P, monospace" font-size="14" fill="#ffb703">{{ampm}}</text></g>
    <text x="200" y="206" font-family="Press Start 2P, monospace" font-size="13" fill="#48cae4">{{weekdayShort}} {{day2}} {{monthShort}}</text>
  </g>
</svg>`,
};

// ── 07 · Cottagecore ───────────────────────────────────────────────────────
const cottageDesign: ClockDesign = {
  id: "cottage",
  name: "Cottagecore",
  subtitle: "Stitched dial",
  widgetType: "clock",
  category: "clock",
  description:
    "Linen card with cross-stitch border, an embroidered analog dial and italic Cormorant time.",
  swatches: ["#f0e4cc", "#6b4a2a", "#b85a4a", "#5a7a4a"],
  viewBox: "610 410 700 260",
  cardWidth: 700,
  cardHeight: 260,
  fonts: ["Cormorant Garamond", "DM Mono"],
  usesSeconds: false,
  slots: {
    time: T,
    weekdayLongLower: T,
    day: T,
    monthLongLower: T,
  },
  frame: `<svg viewBox="610 410 700 260" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <pattern id="cottage-ckd-linen" width="4" height="4" patternUnits="userSpaceOnUse"><rect width="4" height="4" fill="#f7eed5"/><rect width="1" height="4" fill="#d8c8a8" opacity=".55"/><rect y="2" width="4" height="1" fill="#d8c8a8" opacity=".4"/></pattern>
    <pattern id="cottage-ckd-st" width="24" height="24" patternUnits="userSpaceOnUse"><path d="M5 5 L19 19 M19 5 L5 19" stroke="#b85a4a" stroke-width="2.5" stroke-linecap="round"/></pattern>
    <filter id="cottage-ckd-shadow" x="-20%" y="-20%" width="140%" height="160%"><feDropShadow dx="0" dy="14" stdDeviation="20" flood-color="#3a2a14" flood-opacity=".4"/></filter>
  </defs>
  <g transform="translate(610 410)">
    <rect width="700" height="260" fill="url(#cottage-ckd-linen)" stroke="#6b4a2a" stroke-width="2" filter="url(#cottage-ckd-shadow)"/>
    <rect width="700" height="24" fill="url(#cottage-ckd-st)"/>
    <rect y="236" width="700" height="24" fill="url(#cottage-ckd-st)"/>
    <rect width="24" height="260" fill="url(#cottage-ckd-st)"/>
    <rect x="676" width="24" height="260" fill="url(#cottage-ckd-st)"/>
    <g transform="translate(140 130)">
      <circle r="80" fill="#fff8e8" stroke="#6b4a2a" stroke-width="3"/>
      <circle r="80" fill="none" stroke="#b85a4a" stroke-width="1.5" stroke-dasharray="3 5"/>
      <g stroke="#6b4a2a" stroke-width="2"><line x1="0" y1="-72" x2="0" y2="-62"/><line x1="0" y1="72" x2="0" y2="62"/><line x1="-72" y1="0" x2="-62" y2="0"/><line x1="72" y1="0" x2="62" y2="0"/></g>
      <line x1="0" y1="0" x2="20" y2="-38" stroke="#6b4a2a" stroke-width="4" stroke-linecap="round"/>
      <line x1="0" y1="0" x2="-40" y2="18" stroke="#b85a4a" stroke-width="3" stroke-linecap="round"/>
      <circle r="5" fill="#5a7a4a"/>
    </g>
    <text x="250" y="138" font-family="Cormorant Garamond, serif" font-style="italic" font-weight="600" font-size="92" fill="#2a1a08">{{time}}</text>
    <text x="250" y="184" font-family="DM Mono, monospace" font-size="15" fill="#5a7a4a" letter-spacing="6">{{weekdayLongLower}} · {{day}} de {{monthLongLower}} 🌿</text>
  </g>
</svg>`,
};

// ── 08 · Brutalist ─────────────────────────────────────────────────────────
const brutalistDesign: ClockDesign = {
  id: "brutalist",
  name: "Brutalist",
  subtitle: "Mono slab",
  widgetType: "clock",
  category: "clock",
  description:
    "Black slab with orange edge, enormous Anton time and a mono date/zone footer.",
  swatches: ["#1a1a1a", "#f5f5f5", "#ff5a2a", "#c8c2b8"],
  viewBox: "610 410 714 274",
  cardWidth: 714,
  cardHeight: 274,
  fonts: ["Anton", "DM Mono"],
  usesSeconds: true,
  slots: {
    time: T,
    weekdayShort: T,
    day2: T,
    monthShort: T,
    year: T,
    gmt: T,
    seconds: T,
  },
  frame: `<svg viewBox="610 410 714 274" xmlns="http://www.w3.org/2000/svg">
  <g transform="translate(610 410)">
    <rect x="14" y="14" width="700" height="260" fill="#ff5a2a"/>
    <rect width="700" height="260" fill="#1a1a1a"/>
    <rect width="24" height="260" fill="#ff5a2a"/>
    <text x="48" y="62" font-family="DM Mono, monospace" font-size="16" fill="#ff5a2a" letter-spacing="12">// HORA LOCAL</text>
    <text x="40" y="196" font-family="Anton, sans-serif" font-size="150" fill="#f5f5f5" letter-spacing="-2">{{time}}</text>
    <line x1="48" y1="220" x2="652" y2="220" stroke="#3a3a3a"/>
    <text x="48" y="250" font-family="DM Mono, monospace" font-size="16" fill="#c8c2b8" letter-spacing="6">{{weekdayShort}} {{day2}} {{monthShort}} {{year}} · {{gmt}}</text>
    <text x="652" y="250" text-anchor="end" font-family="DM Mono, monospace" font-size="16" fill="#ff5a2a">:{{seconds}}</text>
  </g>
</svg>`,
};

// ── 09 · Anime VHS ─────────────────────────────────────────────────────────
const animeDesign: ClockDesign = {
  id: "anime",
  name: "Anime VHS",
  subtitle: "時刻",
  widgetType: "clock",
  category: "clock",
  description:
    "Pink double-stroke panel with cyan tracking ghost, katakana corner and RGB-split VT323 time.",
  swatches: ["#0a0218", "#ff7ad0", "#7ad0ff", "#fff7c2"],
  viewBox: "610 410 708 260",
  cardWidth: 708,
  cardHeight: 260,
  fonts: ["VT323", "DM Mono"],
  usesSeconds: false,
  slots: {
    time: T,
    weekdayJa: T,
    weekdayShort: T,
    day2: T,
    monthShort: T,
  },
  frame: `<svg viewBox="610 410 708 260" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <filter id="anime-ckd-rgb"><feOffset in="SourceGraphic" dx="-3" dy="0" result="r"/><feColorMatrix in="r" values="1 0 0 0 1   0 0 0 0 0.5   0 0 0 0 0.8   0 0 0 1 0" result="rr"/><feOffset in="SourceGraphic" dx="3" dy="0" result="c"/><feColorMatrix in="c" values="0 0 0 0 0.5   0 0 0 0 0.9   0 0 0 0 1   0 0 0 1 0" result="cc"/><feMerge><feMergeNode in="rr"/><feMergeNode in="cc"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
    <pattern id="anime-ckd-scan" width="100%" height="3" patternUnits="userSpaceOnUse"><rect width="100%" height="1" fill="#ff7ad0" opacity=".12"/></pattern>
  </defs>
  <g transform="translate(610 410)" filter="url(#anime-ckd-rgb)">
    <rect x="6" y="0" width="700" height="260" fill="none" stroke="#7ad0ff" stroke-width="3" opacity=".55"/>
    <rect width="700" height="260" fill="#0a0218" stroke="#ff7ad0" stroke-width="4"/>
    <rect x="10" y="10" width="680" height="240" fill="none" stroke="#7ad0ff" stroke-width="1.5" opacity=".55"/>
    <rect width="700" height="260" fill="url(#anime-ckd-scan)"/>
    <g><rect width="230" height="36" fill="#ff7ad0"/><text x="14" y="26" font-family="DM Mono, monospace" font-size="16" fill="#0a0218" letter-spacing="4">時刻 · TIME</text></g>
    <text x="666" y="28" text-anchor="end" font-family="VT323, monospace" font-size="24" fill="#7ad0ff">●LIVE</text>
    <text x="350" y="186" text-anchor="middle" font-family="VT323, monospace" font-size="130" fill="#fff7c2">{{time}}</text>
    <text x="350" y="228" text-anchor="middle" font-family="VT323, monospace" font-size="26" fill="#7ad0ff" letter-spacing="4">{{weekdayJa}} · {{weekdayShort}} {{day2}} {{monthShort}}</text>
  </g>
</svg>`,
};

// ── 10 · Liquid Glass ──────────────────────────────────────────────────────
const liquidDesign: ClockDesign = {
  id: "liquid",
  name: "Liquid Glass",
  subtitle: "Frosted time",
  widgetType: "clock",
  category: "clock",
  description:
    "Frosted glass chip over a cobalt gradient with translucent time, inner highlight and a soft bokeh.",
  swatches: ["#0a1230", "#1c3a7a", "#9ad4ff", "#ffffff"],
  viewBox: "610 410 700 260",
  cardWidth: 700,
  cardHeight: 260,
  fonts: ["Space Grotesk", "DM Mono"],
  usesSeconds: false,
  slots: {
    gmt: T,
    time: T,
    weekdayLong: T,
    day: T,
    monthShort: T,
    year: T,
  },
  frame: `<svg viewBox="610 410 700 260" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <radialGradient id="liquid-ckd-bg" cx="40%" cy="20%" r="100%"><stop offset="0%" stop-color="#2a4a9a"/><stop offset="60%" stop-color="#0a1230"/><stop offset="100%" stop-color="#020615"/></radialGradient>
    <radialGradient id="liquid-ckd-bok" cx="50%" cy="50%" r="50%"><stop offset="0%" stop-color="#9ad4ff" stop-opacity=".55"/><stop offset="100%" stop-color="#9ad4ff" stop-opacity="0"/></radialGradient>
    <filter id="liquid-ckd-bl"><feGaussianBlur stdDeviation="22"/></filter>
    <filter id="liquid-ckd-shadow" x="-20%" y="-20%" width="140%" height="160%"><feDropShadow dx="0" dy="16" stdDeviation="24" flood-color="#000" flood-opacity=".55"/></filter>
  </defs>
  <g transform="translate(610 410)">
    <rect width="700" height="260" rx="34" fill="url(#liquid-ckd-bg)" stroke="rgba(255,255,255,.22)" stroke-width="1.5" filter="url(#liquid-ckd-shadow)"/>
    <g filter="url(#liquid-ckd-bl)" opacity=".6"><circle cx="140" cy="80" r="120" fill="url(#liquid-ckd-bok)"/><circle cx="580" cy="220" r="140" fill="url(#liquid-ckd-bok)"/></g>
    <rect x="1.5" y="1.5" width="697" height="90" rx="32" fill="#fff" opacity=".05"/>
    <g transform="translate(48 54)"><circle r="8" fill="#9ad4ff"/><circle r="13" fill="none" stroke="#9ad4ff" stroke-width="1.5" opacity=".5"/></g>
    <text x="76" y="62" font-family="Space Grotesk, sans-serif" font-weight="600" font-size="22" fill="#eaf4ff" letter-spacing="3">HORA</text>
    <text x="652" y="62" text-anchor="end" font-family="DM Mono, monospace" font-size="18" fill="#9ad4ff">{{gmt}}</text>
    <text x="350" y="190" text-anchor="middle" font-family="Space Grotesk, sans-serif" font-weight="700" font-size="128" fill="#fff" letter-spacing="2">{{time}}</text>
    <text x="350" y="230" text-anchor="middle" font-family="DM Mono, monospace" font-size="17" fill="#9ad4ff" letter-spacing="6">{{weekdayLong}} · {{day}} {{monthShort}} {{year}}</text>
  </g>
</svg>`,
};

export const CLOCK_DESIGNS: ClockDesign[] = [
  darkDesign,
  flipDesign,
  y2kDesign,
  cyberDesign,
  editorialDesign,
  pixelDesign,
  cottageDesign,
  brutalistDesign,
  animeDesign,
  liquidDesign,
];

/** Render a design's card SVG for the given (already-computed) clock parts. */
export function renderClockDesignSvg(
  design: ClockDesign,
  parts: ClockParts,
): string {
  return renderDesign(design, {
    values: {
      // text slots — substituted verbatim (escape:false)
      hh: parts.hh,
      mm: parts.mm,
      time: parts.time,
      h1: parts.h1,
      h2: parts.h2,
      m1: parts.m1,
      m2: parts.m2,
      seconds: parts.seconds,
      ampm: parts.ampm,
      weekdayLong: parts.weekdayLong,
      weekdayShort: parts.weekdayShort,
      weekdayJa: parts.weekdayJa,
      weekdayLongLower: parts.weekdayLongLower,
      day: parts.day,
      day2: parts.day2,
      monthShort: parts.monthShort,
      monthLong: parts.monthLong,
      monthLongLower: parts.monthLongLower,
      year: parts.year,
      gmt: parts.gmt,
      roman: parts.roman,
    },
  });
}

/**
 * Adapt an INSTALLED community design (a plain {@link SerializableDesign}) to
 * the {@link ClockDesign} view the live renderer expects. The community payload
 * already carries the same `frame`/`slots`/`computed` shape; only the narrowed
 * clock-store metadata needs defaults. `usesSeconds` defaults to `true` so the
 * renderer simply re-rasters every tick (always correct, never stale), and
 * `viewBox` falls back to the card box like the base renderDesign default.
 */
const asClockDesign = (d: SerializableDesign): ClockDesign => ({
  ...d,
  widgetType: "clock",
  category: "clock",
  viewBox: d.viewBox ?? `0 0 ${d.cardWidth} ${d.cardHeight}`,
  usesSeconds: true,
});

export function getClockDesign(
  id: string | undefined,
): ClockDesign | undefined {
  if (!id) {
    return undefined;
  }
  // First-party catalog wins; only fall back to an installed community design
  // (hydrated into the registry by useDesignLibrary) when the id is unknown.
  const local = CLOCK_DESIGNS.find((d) => d.id === id);
  if (local) {
    return local;
  }
  const installed = getInstalledDesign(id, "clock");
  return installed ? asClockDesign(installed) : undefined;
}
